import { contentDelete, contentGet, contentPatch, contentPut } from '../../../../shared/email-activity/content-api'
import { queryString } from '../../../../shared/sso-workspace/auth-api'
import { getBlobAsync } from '../../../../shared/api.service'
import { carryRequestID } from '../../../../shared/error/request-id'

// Staff oversight of API keys (Phase D D8/D9/D11; plan Task 16): content's
// /api/ddd/api-key-sys routes (Task 13) and the usage reads (Task 14), all
// SYS_API_KEY, a person, never an impersonated session (reads included).
// Each resolves to the body; a refusal throws { status, data: { code,
// message } }, so serverMessage(err) shows the server's own sentence.

// ?organization_id&status&last_used=never|within_<N>d|not_<N>d&cursor&limit
// -> { items, next }; `next` is the following page's ?cursor=.
export const listSysKeys = params => contentGet('api-key-sys', params)

// { organization_id, organization, enabled, default_rate_limit_per_minute, live_keys, max_keys }
export const getOrgAccess = organizationID => contentGet(`api-key-sys/organization/${organizationID}/access`)
// The switch (ruling 9): a reason is required.
export const setOrgAccess = (organizationID, enabled, reason) =>
  contentPut(`api-key-sys/organization/${organizationID}/access`, { enabled: !!enabled, reason: String(reason || '').trim() })

export const setRateLimit = (keyID, perMinute) => contentPatch(`api-key-sys/${keyID}`, { rate_limit_per_minute: perMinute })

export const revokeAnyKey = (keyID, reason) => {
  const text = String(reason || '').trim()
  return contentDelete(`api-key-sys/${keyID}`, undefined, text ? { reason: text } : {})
}

// Every organization's usage, or one's (org): ?from&to&org.
export const getSysUsage = ({ from, to, org }) => contentGet('api-key-sys/usage', { from, to, org })

export const usageCsvPath = ({ from, to, org }) => `api/ddd/api-key-sys/usage.csv${queryString({ from, to, org })}`

// A Blob's text (FileReader where Blob.text is missing, as in jsdom).
export const blobText = blob => {
  if (typeof blob.text === 'function') return blob.text()
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => resolve('')
    reader.readAsText(blob)
  })
}

// A refused download's body arrives as a Blob (the request asked for one):
// read it, so the refusal reads like every other ({ status, data }).
const refusal = async err => {
  const body = err && err.error
  let data = body && typeof body === 'object' && !(typeof Blob !== 'undefined' && body instanceof Blob) ? body : null
  if (!data && typeof Blob !== 'undefined' && body instanceof Blob) {
    try {
      data = JSON.parse(await blobText(body))
    } catch (e) {
      data = null
    }
  }
  return carryRequestID({ status: err && err.status, data: data || {} }, err)
}

// The CSV (columns day_utc, organization_id, organization, api_key_id,
// key_name, service, method, route, outcome, requests) downloaded with the
// session: a plain link would carry no token. Content records the export
// (api_usage.exported).
export const downloadUsageCsv = (params, filename) => downloadCsvFile(usageCsvPath(params), filename)

// Any staff CSV on content: GET the path with the session, hand the blob to
// the browser as filename, and resolve to the blob (a caller may read it).
// A refusal throws { status, data }.
export const downloadCsvFile = async (path, filename) => {
  let response
  try {
    response = await getBlobAsync(process.env.REACT_APP_API_CONTENT, path)
  } catch (err) {
    throw await refusal(err)
  }
  const url = URL.createObjectURL(response.data)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
  return response.data
}
